import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { pageActions } from "../../store/page";
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ViewList,
  Delete,
} from "@mui/icons-material";
import Page from "./Page";
import useApi from "../../hooks/useApi";
import useData from "../../hooks/useData";
import useLocalization from "../../hooks/useLocalization";
import { buildSpacesImageUrl } from "../../utils/spacesImageUrl";
import SurveyMetricCard from "../dashboard/SurveyMetricCard";
import BarChart from "../charts/BarChart";
import PieChart from "../charts/PieChart";

// Phase 3 - Step 8: Page structure + filters (no data wired yet)
// This is the skeleton for Survey Analysis page. Data calls will be added in later steps.

const completionStatusOptions = [
  { value: "complete", label: "Complete Only" },
  { value: "all", label: "All" },
  { value: "incomplete", label: "Incomplete Only" },
];

const SurveyAnalysis = () => {
  const location = useLocation();
  const surveyId = location.state?.surveyId || "";
  const projectId = location.state?.projectId;
  const clientId = location.state?.clientId;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { postData } = useApi();
  const { getFromServer } = useData();
  const { distribution } = useLocalization();

  // Filters state (placeholders for now)
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [pageId, setPageId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [questionIds, setQuestionIds] = useState([]);
  const [completionStatus, setCompletionStatus] = useState("complete");

  // Saved views state
  const [savedViews, setSavedViews] = useState([]);
  const [selectedViewId, setSelectedViewId] = useState("");
  const [pendingView, setPendingView] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveMode, setSaveMode] = useState("new"); // "new" or "update"
  const [viewName, setViewName] = useState("");
  const [viewDescription, setViewDescription] = useState("");
  const [viewIsDefault, setViewIsDefault] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [viewToDelete, setViewToDelete] = useState(null);

  // Chart view state
  const [chartViews, setChartViews] = useState({});

  // Group by state
  const [groupBy, setGroupBy] = useState("none");

  // Metrics state
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [metricsError, setMetricsError] = useState(null);

  // Question analysis state
  const [questionAnalysis, setQuestionAnalysis] = useState([]);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  // SKU Matrix state
  const [skuItems, setSkuItems] = useState([]);
  const [skuTotal, setSkuTotal] = useState(0);
  const [skuLimit, setSkuLimit] = useState(50);
  const [skuOffset, setSkuOffset] = useState(0);
  const [loadingSku, setLoadingSku] = useState(false);
  const [skuError, setSkuError] = useState(null);

  // Placeholder option lists (to be loaded from API later)
  const [surveyPages, setSurveyPages] = useState([]);
  const [surveySections, setSurveySections] = useState([]);
  const [surveyQuestions, setSurveyQuestions] = useState([]);

  // Load saved views for survey
  useEffect(() => {
    const loadSavedViews = async () => {
      if (!surveyId) return;

      try {
        const response = await postData({
          url: "/api/entity",
          entityName: "SurveyAnalysis",
          action: "listViews",
          data: { surveyId },
        });

        if (response && response.data) {
          setSavedViews(Array.isArray(response.data) ? response.data : []);

          // Auto-load default view if exists
          const defaultView = response.data.find((v) => v.isDefault);
          if (defaultView) {
            applyView(defaultView);
          }
        }
      } catch (err) {
        console.error("Error loading saved views", err);
      }
    };

    loadSavedViews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  // Load metrics when filters change
  useEffect(() => {
    const loadMetrics = async () => {
      if (!surveyId) return;

      try {
        setLoadingMetrics(true);
        setMetricsError(null);

        const payload = {
          surveyId,
          completionStatus,
        };

        if (dateRange.start && dateRange.end) {
          payload.dateRange = {
            start: dateRange.start,
            end: dateRange.end,
          };
        }

        const response = await postData({
          url: "/api/entity",
          entityName: "SurveyAnalysis",
          action: "getMetrics",
          data: payload,
        });

        if (response && response.data) {
          setMetrics(response.data);
        }
      } catch (err) {
        console.error("Error loading metrics", err);
        setMetricsError("Failed to load metrics");
      } finally {
        setLoadingMetrics(false);
      }
    };

    loadMetrics();
  }, [surveyId, dateRange.start, dateRange.end, completionStatus]);

  // Load pages for survey
  useEffect(() => {
    const loadPages = async () => {
      if (!surveyId) return;
      try {
        const pages = await getFromServer({
          entityName: "SurveyPage",
          filter: { surveyId },
        });
        setSurveyPages(Array.isArray(pages) ? pages : []);
      } catch (err) {
        console.error("Error loading survey pages", err);
        setSurveyPages([]);
      }
    };

    loadPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  // Load sections when page changes
  useEffect(() => {
    const loadSections = async () => {
      if (!pageId) {
        setSurveySections([]);
        setQuestionIds([]);
        return;
      }

      try {
        const sections = await getFromServer({
          entityName: "SurveySection",
          filter: { surveyPageId: pageId },
        });
        // Filter out text sections (they have no questions)
        const filteredSections = Array.isArray(sections)
          ? sections.filter((s) => s.sectionType !== "text")
          : [];
        setSurveySections(filteredSections);
      } catch (err) {
        console.error("Error loading survey sections", err);
        setSurveySections([]);
      }
    };

    loadSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  // Apply pending sectionId after sections are loaded
  useEffect(() => {
    if (pendingView && pendingView.sectionId && surveySections.length > 0) {
      const sectionExists = surveySections.find(
        (s) => (s.id || s._id) === pendingView.sectionId,
      );
      if (sectionExists) {
        setSectionId(pendingView.sectionId);
        setPendingView(null);
      }
    }
  }, [pendingView, surveySections]);

  // Load questions when section changes
  useEffect(() => {
    const loadQuestions = async () => {
      if (!sectionId) {
        setSurveyQuestions([]);
        setQuestionIds([]);
        return;
      }

      try {
        const questions = await getFromServer({
          entityName: "SurveyQuestion",
          filter: { surveySectionId: sectionId },
        });
        setSurveyQuestions(Array.isArray(questions) ? questions : []);
      } catch (err) {
        console.error("Error loading survey questions", err);
        setSurveyQuestions([]);
      }
    };

    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId]);

  // Apply pending questionIds after questions are loaded
  useEffect(() => {
    if (
      pendingView &&
      pendingView.selectedQuestions &&
      surveyQuestions.length > 0
    ) {
      setQuestionIds(pendingView.selectedQuestions);
      if (pendingView.visualizationPreferences) {
        setChartViews(pendingView.visualizationPreferences);
      }
      setPendingView(null);
    }
  }, [pendingView, surveyQuestions]);

  // Load question analysis when filters change
  useEffect(() => {
    const loadAnalysis = async () => {
      if (!surveyId) return;
      if (!sectionId) {
        setQuestionAnalysis([]);
        return;
      }

      // Skip when current section is a SKU section; handled by separate effect
      const currentSection = surveySections.find(
        (s) => (s.id || s._id) === sectionId,
      );
      if (currentSection && currentSection.sectionType === "sku") {
        return;
      }

      try {
        setLoadingAnalysis(true);
        setAnalysisError(null);

        const payload = {
          surveyId,
          sectionId,
          completionStatus,
        };

        if (dateRange.start && dateRange.end) {
          payload.dateRange = {
            start: dateRange.start,
            end: dateRange.end,
          };
        }

        if (questionIds.length > 0) {
          payload.questionIds = questionIds;
        }

        if (groupBy && groupBy !== "none") {
          payload.groupBy = groupBy;
        }

        const response = await postData({
          url: "/api/entity",
          entityName: "SurveyAnalysis",
          action: "getQuestionAnalysis",
          data: payload,
        });

        if (response && response.data) {
          // Ensure percentages are calculated for all data
          const processedData = Array.isArray(response.data)
            ? response.data.map((qa) => {
                if (Array.isArray(qa.data)) {
                  // Image, signature, and text questions return individual values (no count field)
                  // Other question types return grouped values with counts
                  const isIndividualValues =
                    qa.questionType === "text" ||
                    qa.questionType === "image" ||
                    qa.questionType === "beforeAfter" ||
                    qa.questionType === "distribution" ||
                    qa.questionType === "signature";

                  if (isIndividualValues) {
                    // Keep data as-is for individual values
                    return {
                      ...qa,
                      data: qa.data.map((item) => ({
                        ...item,
                        value:
                          qa.questionType === "text"
                            ? String(item.value || "(No answer)")
                            : item.value, // Keep objects for image/signature questions
                      })),
                    };
                  } else {
                    // Calculate percentages for grouped data
                    const total = qa.data.reduce(
                      (sum, item) => sum + (item.count || 0),
                      0,
                    );
                    return {
                      ...qa,
                      data: qa.data.map((item) => ({
                        ...item,
                        value:
                          typeof item.value === "object"
                            ? JSON.stringify(item.value)
                            : String(item.value || "(No answer)"),
                        percentage:
                          total > 0
                            ? Math.round((item.count / total) * 100)
                            : 0,
                      })),
                    };
                  }
                }
                return qa;
              })
            : [];
          setQuestionAnalysis(processedData);
        }
      } catch (err) {
        console.error("Error loading question analysis", err);
        setAnalysisError("Failed to load question analysis");
      } finally {
        setLoadingAnalysis(false);
      }
    };

    loadAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    surveyId,
    sectionId,
    questionIds,
    dateRange.start,
    dateRange.end,
    completionStatus,
    groupBy,
    surveySections,
  ]);

  // Load SKU Matrix for SKU sections
  useEffect(() => {
    const loadSkuMatrix = async () => {
      if (!surveyId || !sectionId) return;
      const currentSection = surveySections.find(
        (s) => (s.id || s._id) === sectionId,
      );
      if (!currentSection || currentSection.sectionType !== "sku") {
        // Clear when switching away
        setSkuItems([]);
        setSkuTotal(0);
        setSkuOffset(0);
        return;
      }

      try {
        setLoadingSku(true);
        setSkuError(null);

        const payload = {
          surveyId,
          sectionId,
          completionStatus,
          limit: skuLimit,
          offset: skuOffset,
        };
        if (dateRange.start && dateRange.end) {
          payload.dateRange = { start: dateRange.start, end: dateRange.end };
        }
        if (questionIds.length > 0) {
          payload.questionIds = questionIds;
        }
        if (groupBy && groupBy !== "none") {
          payload.groupBy = groupBy;
        }

        const response = await postData({
          url: "/api/entity",
          entityName: "SurveyAnalysis",
          action: "getSkuMatrix",
          data: payload,
        });

        const result = response?.data;
        if (result && Array.isArray(result.items)) {
          setSkuItems(result.items);
          setSkuTotal(result.total || result.items.length || 0);
        } else {
          setSkuItems([]);
          setSkuTotal(0);
        }
      } catch (err) {
        console.error("Error loading SKU matrix", err);
        setSkuError("Failed to load SKU matrix");
      } finally {
        setLoadingSku(false);
      }
    };

    loadSkuMatrix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    surveyId,
    sectionId,
    questionIds,
    dateRange.start,
    dateRange.end,
    completionStatus,
    groupBy,
    skuLimit,
    skuOffset,
    surveySections,
  ]);

  const handleQuestionToggle = (id) => {
    setQuestionIds((prev) =>
      prev.includes(id) ? prev.filter((q) => q !== id) : [...prev, id],
    );
  };

  // Helper to map distribution availableForSale ID to label
  const getAvailableForSaleLabel = (id) => {
    if (!distribution?.availableForSale) return String(id);
    const option = distribution.availableForSale.find(
      (opt) => String(opt.id) === String(id),
    );
    return option?.name || String(id);
  };

  // Helper to map distribution actionTaken ID to label
  const getActionTakenLabel = (id) => {
    if (!distribution?.actionTaken) return String(id);
    const option = distribution.actionTaken.find(
      (opt) => String(opt.id) === String(id),
    );
    return option?.name || String(id);
  };

  // Color palette for distribution options
  const getOptionColor = (id, type) => {
    if (type === "availableForSale") {
      return id === "1" ? "#4caf50" : "#f44336"; // Green for YES, Red for NO
    }
    // Action Taken colors - cycle through palette
    const colors = [
      "#2196f3", // Blue
      "#ff9800", // Orange
      "#9c27b0", // Purple
      "#00bcd4", // Cyan
      "#e91e63", // Pink
      "#795548", // Brown
      "#607d8b", // Blue Gray
    ];
    const actionTakenOptions = distribution?.actionTaken || [];
    const index = actionTakenOptions.findIndex(
      (opt) => String(opt.id) === String(id),
    );
    return colors[index % colors.length];
  };

  // Helper function to group results by dimension
  const groupResultsByDimension = (data, dimensionKey) => {
    if (!dimensionKey || !Array.isArray(data)) return { ungrouped: data };

    const grouped = {};
    data.forEach((item) => {
      const dimension = item[dimensionKey] || "Unknown";
      if (!grouped[dimension]) {
        grouped[dimension] = [];
      }
      grouped[dimension].push(item);
    });
    return grouped;
  };

  const applyView = (view) => {
    if (view.filters) {
      if (view.filters.dateRange) {
        setDateRange(view.filters.dateRange);
      }
      if (view.filters.completionStatus) {
        setCompletionStatus(view.filters.completionStatus);
      }
      if (view.filters.groupBy) {
        setGroupBy(view.filters.groupBy);
      }
    }
    if (view.pageId) {
      setPageId(view.pageId);
      // Store view to apply sectionId and questionIds after sections/questions load
      if (
        view.sectionId ||
        view.selectedQuestions ||
        view.visualizationPreferences
      ) {
        setPendingView(view);
      }
    } else {
      // If no pageId, apply directly
      if (view.sectionId) setSectionId(view.sectionId);
      if (view.selectedQuestions) setQuestionIds(view.selectedQuestions);
      if (view.visualizationPreferences)
        setChartViews(view.visualizationPreferences);
    }
    setSelectedViewId(view.id);
  };

  const handleLoadView = async (viewId) => {
    if (!viewId) return;

    try {
      const response = await postData({
        url: "/api/entity",
        entityName: "SurveyAnalysis",
        action: "loadView",
        data: { id: viewId },
      });

      if (response && response.data) {
        applyView(response.data);
      }
    } catch (err) {
      console.error("Error loading view", err);
    }
  };

  const handleSaveView = async () => {
    if (!viewName.trim()) {
      dispatch(
        pageActions.showMessage({
          message: "Please enter a view name",
          status: "error",
        }),
      );
      return;
    }

    try {
      const normalizedPageId = pageId ? String(pageId) : null;
      const normalizedSectionId = sectionId ? String(sectionId) : null;
      const normalizedSelectedQuestions = Array.isArray(questionIds)
        ? questionIds.filter(Boolean).map((id) => String(id))
        : [];

      const normalizedFilters = {
        completionStatus,
      };

      if (dateRange?.start || dateRange?.end) {
        normalizedFilters.dateRange = {};
        if (dateRange?.start)
          normalizedFilters.dateRange.start = dateRange.start;
        if (dateRange?.end) normalizedFilters.dateRange.end = dateRange.end;
      }

      const payload = {
        surveyId,
        name: viewName,
        description: viewDescription,
        pageId: normalizedPageId,
        sectionId: normalizedSectionId,
        selectedQuestions: normalizedSelectedQuestions,
        visualizationPreferences: chartViews,
        filters: normalizedFilters,
        isDefault: viewIsDefault,
      };

      // Include id if updating existing view
      if (saveMode === "update" && selectedViewId) {
        payload.id = selectedViewId;
      }

      const response = await postData({
        url: "/api/entity",
        entityName: "SurveyAnalysis",
        action: "saveView",
        data: payload,
      });

      if (response && response.data) {
        // Reload saved views
        const listResponse = await postData({
          url: "/api/entity",
          entityName: "SurveyAnalysis",
          action: "listViews",
          data: { surveyId },
        });

        if (listResponse && listResponse.data) {
          setSavedViews(
            Array.isArray(listResponse.data) ? listResponse.data : [],
          );
        }

        setSelectedViewId(response.data.id);
        setSaveDialogOpen(false);
        setViewName("");
        setViewDescription("");
        setViewIsDefault(false);
        setSaveMode("new");

        dispatch(
          pageActions.showMessage({
            message:
              saveMode === "update"
                ? "View updated successfully"
                : "View saved successfully",
            status: "success",
          }),
        );
      }
    } catch (err) {
      console.error("Error saving view", err);
      dispatch(
        pageActions.showMessage({
          message: "Failed to save view",
          status: "error",
        }),
      );
    }
  };

  const handleDeleteView = (viewId) => {
    setViewToDelete(viewId);
    setConfirmDialogOpen(true);
  };

  const confirmDeleteView = async () => {
    if (!viewToDelete) return;

    setConfirmDialogOpen(false);

    try {
      await postData({
        url: "/api/entity",
        entityName: "SurveyAnalysis",
        action: "deleteView",
        data: { id: viewToDelete },
      });

      // Reload saved views
      const listResponse = await postData({
        url: "/api/entity",
        entityName: "SurveyAnalysis",
        action: "listViews",
        data: { surveyId },
      });

      if (listResponse && listResponse.data) {
        setSavedViews(
          Array.isArray(listResponse.data) ? listResponse.data : [],
        );
      }

      if (selectedViewId === viewToDelete) {
        setSelectedViewId("");
      }

      setViewToDelete(null);

      dispatch(
        pageActions.showMessage({
          message: "View deleted successfully",
          status: "success",
        }),
      );
    } catch (err) {
      console.error("Error deleting view", err);
      dispatch(
        pageActions.showMessage({
          message: "Failed to delete view",
          status: "error",
        }),
      );
      setViewToDelete(null);
    }
  };

  const handleSetDefault = async (viewId) => {
    try {
      await postData({
        url: "/api/entity",
        entityName: "SurveyAnalysis",
        action: "setDefault",
        data: { id: viewId, surveyId },
      });

      // Reload saved views to update isDefault flags
      const listResponse = await postData({
        url: "/api/entity",
        entityName: "SurveyAnalysis",
        action: "listViews",
        data: { surveyId },
      });

      if (listResponse && listResponse.data) {
        setSavedViews(
          Array.isArray(listResponse.data) ? listResponse.data : [],
        );
      }
      dispatch(
        pageActions.showMessage({
          message: "Default view updated",
          status: "success",
        }),
      );
    } catch (err) {
      console.error("Error setting default view", err);
      dispatch(
        pageActions.showMessage({
          message: "Failed to set default view",
          status: "error",
        }),
      );
    }
  };

  return (
    <Page
      pageName="surveyAnalysis"
      includeToolbar
      items={[
        {
          name: "survey",
          iconName: "Assessment",
        },
      ]}
      onToolItemClick={(item) => {
        if (item === "survey") {
          navigate("/survey", {
            state: { surveyId, projectId, clientId },
          });
        }
      }}
    >
      <Stack
        spacing={2}
        sx={{ width: "100%" }}
      >
        {/* Saved Views */}
        <Paper sx={{ p: 2, width: "100%" }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <FormControl sx={{ minWidth: 240, flex: 1 }}>
              <InputLabel id="saved-view-label">Saved View</InputLabel>
              <Select
                labelId="saved-view-label"
                label="Saved View"
                value={selectedViewId}
                onChange={(e) => handleLoadView(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {savedViews.map((view) => (
                  <MenuItem
                    key={view.id || view._id}
                    value={view.id || view._id}
                  >
                    {view.name} {view.isDefault ? "(Default)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedViewId && (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const currentView = savedViews.find(
                      (v) => v.id === selectedViewId,
                    );
                    if (currentView) {
                      setViewName(currentView.name);
                      setViewDescription(currentView.description || "");
                      setViewIsDefault(currentView.isDefault || false);
                      setSaveMode("update");
                      setSaveDialogOpen(true);
                    }
                  }}
                >
                  Update View
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleSetDefault(selectedViewId)}
                >
                  Set as Default
                </Button>
                <IconButton
                  color="error"
                  onClick={() => handleDeleteView(selectedViewId)}
                >
                  <Delete />
                </IconButton>
              </>
            )}

            <Button
              variant="contained"
              onClick={() => {
                setViewName("");
                setViewDescription("");
                setViewIsDefault(false);
                setSaveMode("new");
                setSaveDialogOpen(true);
              }}
            >
              Save as New View
            </Button>
          </Stack>
        </Paper>

        {/* Save View Dialog */}
        <Dialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {saveMode === "update" ? "Update View" : "Save New View"}
          </DialogTitle>
          <DialogContent>
            <Stack
              spacing={2}
              sx={{ mt: 1 }}
            >
              <TextField
                label="View Name"
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Description"
                value={viewDescription}
                onChange={(e) => setViewDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={viewIsDefault}
                    onChange={(e) => setViewIsDefault(e.target.checked)}
                  />
                }
                label="Set as default view for this survey"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveView}
              variant="contained"
            >
              {saveMode === "update" ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirm Delete Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Saved View</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this saved view? This action
              cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={confirmDeleteView}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Filters */}
        <Paper sx={{ p: 2, width: "100%" }}>
          <Grid
            container
            spacing={2}
          >
            <Grid item>
              <TextField
                label="Date Start"
                type="date"
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ minWidth: 200 }}
                value={dateRange.start}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, start: e.target.value }))
                }
              />
            </Grid>
            <Grid item>
              <TextField
                label="Date End"
                type="date"
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ minWidth: 200 }}
                value={dateRange.end}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, end: e.target.value }))
                }
              />
            </Grid>

            <Grid item>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="page-select-label">Page</InputLabel>
                <Select
                  labelId="page-select-label"
                  label="Page"
                  value={pageId}
                  onChange={(e) => {
                    setPageId(e.target.value);
                    setSectionId("");
                    setQuestionIds([]);
                  }}
                >
                  <MenuItem value="">All Pages</MenuItem>
                  {surveyPages.map((page) => (
                    <MenuItem
                      key={page.id || page._id}
                      value={page.id || page._id}
                    >
                      {page.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="section-select-label">Section</InputLabel>
                <Select
                  labelId="section-select-label"
                  label="Section"
                  value={sectionId}
                  onChange={(e) => {
                    setSectionId(e.target.value);
                    setQuestionIds([]);
                  }}
                  disabled={!pageId}
                >
                  <MenuItem value="">All Sections</MenuItem>
                  {surveySections.map((section) => (
                    <MenuItem
                      key={section.id || section._id}
                      value={section.id || section._id}
                    >
                      {section.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="completion-select-label">
                  Completion Status
                </InputLabel>
                <Select
                  labelId="completion-select-label"
                  label="Completion Status"
                  value={completionStatus}
                  onChange={(e) => setCompletionStatus(e.target.value)}
                >
                  {completionStatusOptions.map((opt) => (
                    <MenuItem
                      key={opt.value}
                      value={opt.value}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="groupby-select-label">Group By</InputLabel>
                <Select
                  labelId="groupby-select-label"
                  label="Group By"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="territory">Rep</MenuItem>
                  <MenuItem value="province">Province</MenuItem>
                  <MenuItem value="banner">Banner</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Question selection chips */}
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1 }}
            >
              Questions (enabled when a section is selected)
            </Typography>
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
            >
              {sectionId && surveyQuestions.length === 0 && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                >
                  No questions available for this section
                </Typography>
              )}
              {sectionId &&
                surveyQuestions.map((q) => (
                  <Chip
                    key={q.id || q._id}
                    label={q.name}
                    color={
                      questionIds.includes(q.id || q._id)
                        ? "primary"
                        : "default"
                    }
                    variant={
                      questionIds.includes(q.id || q._id)
                        ? "filled"
                        : "outlined"
                    }
                    onClick={() => handleQuestionToggle(q.id || q._id)}
                    sx={{ borderRadius: "4px" }}
                  />
                ))}
              {!sectionId && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                >
                  Select a section to choose questions
                </Typography>
              )}
            </Stack>
          </Box>
        </Paper>

        {/* Summary Metrics */}
        <Paper sx={{ p: 2, width: "100%" }}>
          <Typography
            variant="h6"
            sx={{ mb: 1 }}
          >
            Summary Metrics
          </Typography>
          {metricsError && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mb: 1 }}
            >
              {metricsError}
            </Typography>
          )}
          {!surveyId && (
            <Typography
              variant="body2"
              color="textSecondary"
            >
              No survey selected. Please open analysis from a survey.
            </Typography>
          )}
          {surveyId && (
            <Grid
              container
              spacing={2}
            >
              <Grid item>
                <SurveyMetricCard
                  label="Total Responses"
                  value={metrics?.totalResponses || 0}
                  isLoading={loadingMetrics}
                />
              </Grid>
              <Grid item>
                <SurveyMetricCard
                  label="Completed Responses"
                  value={metrics?.completedResponses || 0}
                  isLoading={loadingMetrics}
                />
              </Grid>
              <Grid item>
                <SurveyMetricCard
                  label="Completion Rate"
                  value={metrics?.completionRate || 0}
                  unit="%"
                  isLoading={loadingMetrics}
                />
              </Grid>
              <Grid item>
                <SurveyMetricCard
                  label="Avg Completion Time"
                  value={metrics?.avgCompletionTime || "0:00"}
                  isLoading={loadingMetrics}
                />
              </Grid>
            </Grid>
          )}
        </Paper>

        {/* SKU Matrix or Question Analysis */}
        <Paper sx={{ p: 2, width: "100%" }}>
          {(() => {
            const currentSection = surveySections.find(
              (s) => (s.id || s._id) === sectionId,
            );
            const isSkuSection = currentSection?.sectionType === "sku";
            if (!sectionId) {
              return (
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Select a section to view analysis
                </Typography>
              );
            }
            if (isSkuSection) {
              return (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2 }}
                  >
                    SKU Matrix
                  </Typography>
                  {loadingSku ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Loading SKU matrix...
                    </Typography>
                  ) : skuError ? (
                    <Typography
                      variant="body2"
                      color="error"
                    >
                      {skuError}
                    </Typography>
                  ) : skuItems.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      No SKU data for selected filters
                    </Typography>
                  ) : (
                    <Box>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>
                                SKU
                              </TableCell>
                              {groupBy && groupBy !== "none" && (
                                <TableCell sx={{ fontWeight: 600 }}>
                                  {groupBy.charAt(0).toUpperCase() +
                                    groupBy.slice(1)}
                                </TableCell>
                              )}
                              {(
                                (questionIds.length > 0
                                  ? surveyQuestions.filter((q) =>
                                      questionIds.includes(q.id || q._id),
                                    )
                                  : surveyQuestions) || []
                              ).map((q) => (
                                <TableCell
                                  key={q.id || q._id}
                                  sx={{ fontWeight: 600 }}
                                >
                                  {q.name}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {skuItems.map((row, rIdx) => {
                              const qMap = new Map(
                                (row.questions || []).map((q) => [
                                  q.questionId,
                                  q,
                                ]),
                              );
                              const columns =
                                questionIds.length > 0
                                  ? surveyQuestions.filter((q) =>
                                      questionIds.includes(q.id || q._id),
                                    )
                                  : surveyQuestions;
                              return (
                                <TableRow
                                  key={rIdx}
                                  hover
                                >
                                  <TableCell>
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {row.sku?.name || "(Unnamed)"}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "text.secondary" }}
                                      >
                                        UPC: {row.sku?.upc || "-"} | Code:{" "}
                                        {row.sku?.clientCode || "-"}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  {groupBy && groupBy !== "none" && (
                                    <TableCell>
                                      {row.groupValue || "-"}
                                    </TableCell>
                                  )}
                                  {columns.map((q) => {
                                    const qid = (q.id || q._id)?.toString();
                                    const cell = qMap.get(qid);
                                    const type = q.questionType;
                                    let content = "-";
                                    if (cell) {
                                      const m = cell.metrics || {};
                                      if (
                                        type === "select" ||
                                        type === "radio" ||
                                        type === "checkbox" ||
                                        type === "multiselect"
                                      ) {
                                        const opts = Array.isArray(m.options)
                                          ? m.options
                                          : [];
                                        const top = opts
                                          .slice(0, 2)
                                          .map(
                                            (o) =>
                                              `${o.value} ${
                                                o.percentage ?? 0
                                              }% (${o.count ?? 0})`,
                                          )
                                          .join(", ");
                                        content =
                                          top ||
                                          `${m.totalResponses || 0} responses`;
                                      } else if (
                                        type === "number" ||
                                        type === "currency"
                                      ) {
                                        const prefix =
                                          type === "currency" ? "$" : "";
                                        const avg =
                                          m.avg != null
                                            ? Number(m.avg).toFixed(2)
                                            : "N/A";
                                        content = `Min ${prefix}${
                                          m.min ?? "N/A"
                                        } / Max ${prefix}${
                                          m.max ?? "N/A"
                                        } / Avg ${prefix}${avg}`;
                                      } else if (type === "text") {
                                        content = `${m.count || 0} notes`;
                                      } else if (
                                        type === "date" ||
                                        type === "time"
                                      ) {
                                        content = `${m.earliest || "-"} → ${
                                          m.latest || "-"
                                        } (${m.count || 0})`;
                                      } else if (type === "distribution") {
                                        // Distribution: show horizontal bars for availableForSale and actionTaken
                                        const afs = Array.isArray(
                                          m.availableForSale,
                                        )
                                          ? m.availableForSale
                                          : [];
                                        const at = Array.isArray(m.actionTaken)
                                          ? m.actionTaken
                                          : [];

                                        content = (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: 1,
                                            }}
                                          >
                                            {/* Available For Sale Bar */}
                                            <Box>
                                              <Typography
                                                variant="caption"
                                                sx={{ fontWeight: 600 }}
                                              >
                                                Available For Sale
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  height: 24,
                                                  gap: 0,
                                                  borderRadius: 1,
                                                  overflow: "hidden",
                                                }}
                                              >
                                                {afs.map((o) => (
                                                  <Box
                                                    key={o.value}
                                                    sx={{
                                                      width: `${o.percentage}%`,
                                                      backgroundColor:
                                                        getOptionColor(
                                                          o.value,
                                                          "availableForSale",
                                                        ),
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      color: "white",
                                                      fontSize: "0.7rem",
                                                      fontWeight: 600,
                                                      padding: "0 4px",
                                                    }}
                                                  >
                                                    {o.percentage > 10 &&
                                                      `${o.percentage}%`}
                                                  </Box>
                                                ))}
                                              </Box>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  gap: 2,
                                                  mt: 0.5,
                                                }}
                                              >
                                                {afs.map((o) => (
                                                  <Typography
                                                    key={o.value}
                                                    variant="caption"
                                                    sx={{ fontSize: "0.65rem" }}
                                                  >
                                                    <span
                                                      style={{
                                                        display: "inline-block",
                                                        width: 12,
                                                        height: 12,
                                                        backgroundColor:
                                                          getOptionColor(
                                                            o.value,
                                                            "availableForSale",
                                                          ),
                                                        marginRight: 4,
                                                        borderRadius: 2,
                                                      }}
                                                    />
                                                    {getAvailableForSaleLabel(
                                                      o.value,
                                                    )}
                                                    : {o.percentage}%
                                                  </Typography>
                                                ))}
                                              </Box>
                                            </Box>

                                            {/* Action Taken Bar */}
                                            {at.length > 0 && (
                                              <Tooltip
                                                title={
                                                  <Box
                                                    sx={{ fontSize: "0.7rem" }}
                                                  >
                                                    {at.map((o) => (
                                                      <Box
                                                        key={o.value}
                                                        sx={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: 1,
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            display:
                                                              "inline-block",
                                                            width: 10,
                                                            height: 10,
                                                            backgroundColor:
                                                              getOptionColor(
                                                                o.value,
                                                                "actionTaken",
                                                              ),
                                                            borderRadius: 1,
                                                          }}
                                                        />
                                                        {getActionTakenLabel(
                                                          o.value,
                                                        )}
                                                        : {o.percentage}%
                                                      </Box>
                                                    ))}
                                                  </Box>
                                                }
                                              >
                                                <Box>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{ fontWeight: 600 }}
                                                  >
                                                    Action Taken
                                                  </Typography>
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      height: 24,
                                                      gap: 0,
                                                      borderRadius: 1,
                                                      overflow: "hidden",
                                                      cursor: "help",
                                                    }}
                                                  >
                                                    {at.map((o) => (
                                                      <Box
                                                        key={o.value}
                                                        sx={{
                                                          width: `${o.percentage}%`,
                                                          backgroundColor:
                                                            getOptionColor(
                                                              o.value,
                                                              "actionTaken",
                                                            ),
                                                          display: "flex",
                                                          alignItems: "center",
                                                          justifyContent:
                                                            "center",
                                                          color: "white",
                                                          fontSize: "0.7rem",
                                                          fontWeight: 600,
                                                          padding: "0 4px",
                                                        }}
                                                      >
                                                        {o.percentage > 10 &&
                                                          `${o.percentage}%`}
                                                      </Box>
                                                    ))}
                                                  </Box>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      color: "primary.main",
                                                      fontSize: "0.65rem",
                                                      mt: 0.5,
                                                      display: "block",
                                                    }}
                                                  >
                                                    Hover for details
                                                  </Typography>
                                                </Box>
                                              </Tooltip>
                                            )}
                                          </Box>
                                        );
                                      } else {
                                        content =
                                          m.totalResponses != null
                                            ? `${m.totalResponses} responses`
                                            : "-";
                                      }
                                    }
                                    return (
                                      <TableCell key={qid}>{content}</TableCell>
                                    );
                                  })}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ mt: 2 }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Showing {Math.min(skuOffset + 1, skuTotal)}–
                          {Math.min(skuOffset + skuLimit, skuTotal)} of{" "}
                          {skuTotal}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={skuOffset === 0}
                          onClick={() =>
                            setSkuOffset(Math.max(skuOffset - skuLimit, 0))
                          }
                        >
                          Prev
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={skuOffset + skuLimit >= skuTotal}
                          onClick={() => setSkuOffset(skuOffset + skuLimit)}
                        >
                          Next
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </Box>
              );
            }

            // Default question analysis UI
            return (
              <>
                <Typography
                  variant="h6"
                  sx={{ mb: 2 }}
                >
                  Question Analysis
                </Typography>
                {analysisError && (
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ mb: 2 }}
                  >
                    {analysisError}
                  </Typography>
                )}
                {!sectionId && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                  >
                    Select a section to view question analysis
                  </Typography>
                )}
                {loadingAnalysis && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                  >
                    Loading question analysis...
                  </Typography>
                )}
                {sectionId &&
                  !loadingAnalysis &&
                  questionAnalysis.length === 0 && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                    >
                      No responses available for the selected questions
                    </Typography>
                  )}
                {sectionId &&
                  !loadingAnalysis &&
                  questionAnalysis.length > 0 && (
                    <Stack spacing={3}>
                      {questionAnalysis.map((qa) => (
                        <Paper
                          key={qa.questionId}
                          sx={{ p: 2, bgcolor: "grey.50" }}
                          variant="outlined"
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ mb: 1, fontWeight: 600 }}
                          >
                            {qa.questionName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ mb: 2, display: "block" }}
                          >
                            Type: {qa.questionType} | Responses:{" "}
                            {qa.totalResponses}
                          </Typography>

                          {/* Chart toggle for choice-based questions */}
                          {(qa.questionType === "select" ||
                            qa.questionType === "radio" ||
                            qa.questionType === "checkbox" ||
                            qa.questionType === "multiselect") &&
                            Array.isArray(qa.data) &&
                            qa.data.length > 0 && (
                              <Box
                                sx={{
                                  mb: 2,
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <ToggleButtonGroup
                                  value={chartViews[qa.questionId] || "list"}
                                  exclusive
                                  onChange={(e, newView) => {
                                    if (newView !== null) {
                                      setChartViews({
                                        ...chartViews,
                                        [qa.questionId]: newView,
                                      });
                                    }
                                  }}
                                  size="small"
                                >
                                  <ToggleButton value="list">
                                    <ViewList fontSize="small" />
                                  </ToggleButton>
                                  <ToggleButton value="bar">
                                    <BarChartIcon fontSize="small" />
                                  </ToggleButton>
                                  <ToggleButton value="pie">
                                    <PieChartIcon fontSize="small" />
                                  </ToggleButton>
                                </ToggleButtonGroup>
                              </Box>
                            )}

                          {/* Display based on question type */}
                          {(qa.questionType === "select" ||
                            qa.questionType === "radio") &&
                            chartViews[qa.questionId] === "bar" && (
                              <BarChart
                                data={qa.data}
                                color={
                                  qa.questionType === "select" ||
                                  qa.questionType === "radio"
                                    ? "#1976d2"
                                    : "#9c27b0"
                                }
                              />
                            )}

                          {(qa.questionType === "select" ||
                            qa.questionType === "radio") &&
                            chartViews[qa.questionId] === "pie" && (
                              <PieChart data={qa.data} />
                            )}

                          {(qa.questionType === "select" ||
                            qa.questionType === "radio") &&
                            (!chartViews[qa.questionId] ||
                              chartViews[qa.questionId] === "list") && (
                              <Stack
                                spacing={groupBy && groupBy !== "none" ? 2 : 1}
                              >
                                {groupBy && groupBy !== "none"
                                  ? Object.entries(
                                      groupResultsByDimension(qa.data, groupBy),
                                    ).map(([dimension, items]) => (
                                      <Box key={dimension}>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            mb: 1,
                                            color: "primary.main",
                                          }}
                                        >
                                          {dimension}
                                        </Typography>
                                        <Stack
                                          spacing={0.5}
                                          sx={{ ml: 1 }}
                                        >
                                          {items.map((item, idx) => (
                                            <Box
                                              key={idx}
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 2,
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{ minWidth: 100 }}
                                              >
                                                {String(
                                                  item.value || "(No answer)",
                                                )}
                                              </Typography>
                                              <Box
                                                sx={{
                                                  flex: 1,
                                                  height: 20,
                                                  bgcolor: "primary.main",
                                                  borderRadius: 1,
                                                  width: `${
                                                    item.percentage || 0
                                                  }%`,
                                                }}
                                              />
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  minWidth: 80,
                                                  textAlign: "right",
                                                  fontSize: "0.85rem",
                                                }}
                                              >
                                                {item.count || 0} (
                                                {item.percentage || 0}%)
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Stack>
                                      </Box>
                                    ))
                                  : qa.data.map((item, idx) => (
                                      <Box
                                        key={idx}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ minWidth: 120 }}
                                        >
                                          {String(item.value || "(No answer)")}
                                        </Typography>
                                        <Box
                                          sx={{
                                            flex: 1,
                                            height: 24,
                                            bgcolor: "primary.main",
                                            borderRadius: 1,
                                            width: `${item.percentage}%`,
                                          }}
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            minWidth: 80,
                                            textAlign: "right",
                                          }}
                                        >
                                          {item.count} ({item.percentage}%)
                                        </Typography>
                                      </Box>
                                    ))}
                              </Stack>
                            )}

                          {(qa.questionType === "checkbox" ||
                            qa.questionType === "multiselect") &&
                            chartViews[qa.questionId] === "bar" && (
                              <BarChart
                                data={qa.data}
                                color="#9c27b0"
                              />
                            )}

                          {(qa.questionType === "checkbox" ||
                            qa.questionType === "multiselect") &&
                            chartViews[qa.questionId] === "pie" && (
                              <PieChart data={qa.data} />
                            )}

                          {(qa.questionType === "checkbox" ||
                            qa.questionType === "multiselect") &&
                            (!chartViews[qa.questionId] ||
                              chartViews[qa.questionId] === "list") && (
                              <Stack
                                spacing={groupBy && groupBy !== "none" ? 2 : 1}
                              >
                                {groupBy && groupBy !== "none"
                                  ? Object.entries(
                                      groupResultsByDimension(qa.data, groupBy),
                                    ).map(([dimension, items]) => (
                                      <Box key={dimension}>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            mb: 1,
                                            color: "secondary.main",
                                          }}
                                        >
                                          {dimension}
                                        </Typography>
                                        <Stack
                                          spacing={0.5}
                                          sx={{ ml: 1 }}
                                        >
                                          {items.map((item, idx) => (
                                            <Box
                                              key={idx}
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 2,
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{ minWidth: 100 }}
                                              >
                                                {String(
                                                  item.value || "(No answer)",
                                                )}
                                              </Typography>
                                              <Box
                                                sx={{
                                                  flex: 1,
                                                  height: 20,
                                                  bgcolor: "secondary.main",
                                                  borderRadius: 1,
                                                  width: `${
                                                    item.percentage || 0
                                                  }%`,
                                                }}
                                              />
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  minWidth: 80,
                                                  textAlign: "right",
                                                  fontSize: "0.85rem",
                                                }}
                                              >
                                                {item.count || 0} (
                                                {item.percentage || 0}%)
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Stack>
                                      </Box>
                                    ))
                                  : qa.data.map((item, idx) => (
                                      <Box
                                        key={idx}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ minWidth: 120 }}
                                        >
                                          {String(item.value || "(No answer)")}
                                        </Typography>
                                        <Box
                                          sx={{
                                            flex: 1,
                                            height: 24,
                                            bgcolor: "secondary.main",
                                            borderRadius: 1,
                                            width: `${item.percentage}%`,
                                          }}
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            minWidth: 80,
                                            textAlign: "right",
                                          }}
                                        >
                                          {item.count} ({item.percentage}%)
                                        </Typography>
                                      </Box>
                                    ))}
                              </Stack>
                            )}

                          {(qa.questionType === "number" ||
                            qa.questionType === "currency") &&
                            qa.data &&
                            typeof qa.data === "object" &&
                            !Array.isArray(qa.data) && (
                              <Grid
                                container
                                spacing={2}
                              >
                                <Grid item>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    Min:{" "}
                                    <strong>
                                      {qa.questionType === "currency"
                                        ? "$"
                                        : ""}
                                      {qa.data.min ?? "N/A"}
                                    </strong>
                                  </Typography>
                                </Grid>
                                <Grid item>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    Max:{" "}
                                    <strong>
                                      {qa.questionType === "currency"
                                        ? "$"
                                        : ""}
                                      {qa.data.max ?? "N/A"}
                                    </strong>
                                  </Typography>
                                </Grid>
                                <Grid item>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    Avg:{" "}
                                    <strong>
                                      {qa.questionType === "currency"
                                        ? "$"
                                        : ""}
                                      {qa.data.avg != null
                                        ? qa.data.avg.toFixed(2)
                                        : "N/A"}
                                    </strong>
                                  </Typography>
                                </Grid>
                              </Grid>
                            )}

                          {(qa.questionType === "number" ||
                            qa.questionType === "currency") &&
                            Array.isArray(qa.data) &&
                            qa.data.length > 0 && (
                              <Stack spacing={2}>
                                {qa.data.map((g, idx) => (
                                  <Box key={idx}>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, mb: 1 }}
                                    >
                                      {groupBy && groupBy !== "none"
                                        ? `${groupBy}: ${
                                            g[groupBy] || "Unknown"
                                          }`
                                        : "Group"}
                                    </Typography>
                                    <Grid
                                      container
                                      spacing={2}
                                    >
                                      <Grid item>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          Min:{" "}
                                          <strong>
                                            {qa.questionType === "currency"
                                              ? "$"
                                              : ""}
                                            {g.min ?? "N/A"}
                                          </strong>
                                        </Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          Max:{" "}
                                          <strong>
                                            {qa.questionType === "currency"
                                              ? "$"
                                              : ""}
                                            {g.max ?? "N/A"}
                                          </strong>
                                        </Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          Avg:{" "}
                                          <strong>
                                            {qa.questionType === "currency"
                                              ? "$"
                                              : ""}
                                            {g.avg != null
                                              ? Number(g.avg).toFixed(2)
                                              : "N/A"}
                                          </strong>
                                        </Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          Count: <strong>{g.count ?? 0}</strong>
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                ))}
                              </Stack>
                            )}

                          {(qa.questionType === "date" ||
                            qa.questionType === "time") &&
                            qa.data &&
                            typeof qa.data === "object" &&
                            !Array.isArray(qa.data) && (
                              <Grid
                                container
                                spacing={2}
                              >
                                <Grid item>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    Earliest:{" "}
                                    <strong>
                                      {qa.data.earliest
                                        ? qa.questionType === "date"
                                          ? new Date(
                                              qa.data.earliest,
                                            ).toLocaleDateString()
                                          : new Date(
                                              qa.data.earliest,
                                            ).toLocaleTimeString()
                                        : "N/A"}
                                    </strong>
                                  </Typography>
                                </Grid>
                                <Grid item>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    Latest:{" "}
                                    <strong>
                                      {qa.data.latest
                                        ? qa.questionType === "date"
                                          ? new Date(
                                              qa.data.latest,
                                            ).toLocaleDateString()
                                          : new Date(
                                              qa.data.latest,
                                            ).toLocaleTimeString()
                                        : "N/A"}
                                    </strong>
                                  </Typography>
                                </Grid>
                              </Grid>
                            )}

                          {qa.questionType === "text" &&
                            Array.isArray(qa.data) && (
                              <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
                                {groupBy && groupBy !== "none" ? (
                                  Object.entries(
                                    groupResultsByDimension(qa.data, groupBy),
                                  ).map(([dimension, items]) => (
                                    <Box
                                      key={dimension}
                                      sx={{ mb: 1.5 }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          mb: 0.5,
                                          color: "text.secondary",
                                        }}
                                      >
                                        {dimension}
                                      </Typography>
                                      {items.slice(0, 10).map((item, idx) => (
                                        <Typography
                                          key={idx}
                                          variant="body2"
                                          sx={{
                                            mb: 0.5,
                                            pl: 1,
                                            borderLeft: 2,
                                            borderColor: "grey.300",
                                          }}
                                        >
                                          {String(item.value)}
                                        </Typography>
                                      ))}
                                      {items.length > 10 && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ mt: 0.5, display: "block" }}
                                        >
                                          ... and {items.length - 10} more
                                          responses
                                        </Typography>
                                      )}
                                    </Box>
                                  ))
                                ) : (
                                  <>
                                    {qa.data.slice(0, 10).map((item, idx) => (
                                      <Typography
                                        key={idx}
                                        variant="body2"
                                        sx={{
                                          mb: 0.5,
                                          pl: 1,
                                          borderLeft: 2,
                                          borderColor: "grey.300",
                                        }}
                                      >
                                        {String(item.value)}
                                      </Typography>
                                    ))}
                                    {qa.data.length > 10 && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 1, display: "block" }}
                                      >
                                        ... and {qa.data.length - 10} more
                                        responses
                                      </Typography>
                                    )}
                                  </>
                                )}
                              </Box>
                            )}

                          {qa.questionType === "image" &&
                            Array.isArray(qa.data) &&
                            qa.data.length > 0 && (
                              <Box>
                                <Grid
                                  container
                                  spacing={1}
                                  sx={{ mb: 2 }}
                                >
                                  {qa.data.slice(0, 12).map((item, idx) => {
                                    // item.value should now be the proper object/array from backend
                                    let filename = null;

                                    // Handle different value structures:
                                    // - Simple image: [{fileName: "...", comment: ""}]
                                    // - Distribution/beforeAfter: {entryPhoto: [...], ...}
                                    if (
                                      Array.isArray(item.value) &&
                                      item.value.length > 0
                                    ) {
                                      const firstItem = item.value[0];
                                      if (typeof firstItem === "string") {
                                        filename = firstItem;
                                      } else if (firstItem?.fileName) {
                                        filename = firstItem.fileName;
                                      }
                                    } else if (
                                      item.value &&
                                      typeof item.value === "object"
                                    ) {
                                      // Distribution question: check for entryPhoto or actionPhoto
                                      if (
                                        Array.isArray(item.value.entryPhoto) &&
                                        item.value.entryPhoto.length > 0
                                      ) {
                                        const entry = item.value.entryPhoto[0];
                                        filename =
                                          typeof entry === "string"
                                            ? entry
                                            : entry?.fileName;
                                      } else if (
                                        Array.isArray(item.value.actionPhoto) &&
                                        item.value.actionPhoto.length > 0
                                      ) {
                                        const action =
                                          item.value.actionPhoto[0];
                                        filename =
                                          typeof action === "string"
                                            ? action
                                            : action?.fileName;
                                      } else if (
                                        Array.isArray(item.value.before) &&
                                        item.value.before.length > 0
                                      ) {
                                        const before = item.value.before[0];
                                        filename =
                                          typeof before === "string"
                                            ? before
                                            : before?.fileName;
                                      } else if (
                                        Array.isArray(item.value.after) &&
                                        item.value.after.length > 0
                                      ) {
                                        const after = item.value.after[0];
                                        filename =
                                          typeof after === "string"
                                            ? after
                                            : after?.fileName;
                                      }
                                    }

                                    const imageUrl = filename
                                      ? buildSpacesImageUrl(filename, {
                                          defaultFolder: "survey-images",
                                        })
                                      : null;

                                    return (
                                      <Grid
                                        item
                                        xs={6}
                                        sm={4}
                                        md={3}
                                        key={idx}
                                      >
                                        <Box>
                                          <Box
                                            component="img"
                                            src={imageUrl}
                                            alt={`Image ${idx + 1}`}
                                            sx={{
                                              width: "100%",
                                              height: 120,
                                              objectFit: "cover",
                                              borderRadius: 1,
                                              backgroundColor: "grey.200",
                                              cursor: "pointer",
                                              border: "1px solid",
                                              borderColor: "divider",
                                              transition:
                                                "transform 0.2s, box-shadow 0.2s",
                                              "&:hover": {
                                                transform: "scale(1.02)",
                                                boxShadow: 1,
                                              },
                                            }}
                                            onClick={() => {
                                              // Open full image in new window
                                              if (imageUrl) {
                                                window.open(imageUrl, "_blank");
                                              }
                                            }}
                                          />
                                          {/* Image context info */}
                                          {item.locationName && (
                                            <Box sx={{ pt: 0.5 }}>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                  display: "block",
                                                  mb: 0.3,
                                                }}
                                              >
                                                {item.callDate}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  display: "block",
                                                  fontWeight: 600,
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                }}
                                              >
                                                {item.locationName} (
                                                {item.locationNumber})
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block" }}
                                              >
                                                {item.city}, {item.province}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block" }}
                                              >
                                                {item.userName}
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Showing {Math.min(12, qa.data.length)} of{" "}
                                  {qa.data.length} images
                                  {qa.data.length > 12 && (
                                    <Button
                                      size="small"
                                      sx={{ ml: 1 }}
                                      onClick={() => {
                                        navigate("/surveyimages", {
                                          state: {
                                            surveyId,
                                            projectId,
                                            clientId,
                                          },
                                        });
                                      }}
                                    >
                                      View All
                                    </Button>
                                  )}
                                </Typography>
                              </Box>
                            )}

                          {qa.questionType === "signature" &&
                            Array.isArray(qa.data) &&
                            qa.data.length > 0 && (
                              <Box>
                                <Grid
                                  container
                                  spacing={1}
                                  sx={{ mb: 2 }}
                                >
                                  {qa.data.slice(0, 12).map((item, idx) => {
                                    // Signature value is {name, position, signature} where signature is base64
                                    let signatureDataUrl = null;
                                    let name = "";
                                    let position = "";

                                    if (
                                      item.value &&
                                      typeof item.value === "object"
                                    ) {
                                      signatureDataUrl = item.value.signature;
                                      name = item.value.name || "";
                                      position = item.value.position || "";
                                    }

                                    return (
                                      <Grid
                                        item
                                        xs={6}
                                        sm={4}
                                        md={3}
                                        key={idx}
                                      >
                                        <Box>
                                          <Box
                                            component="img"
                                            src={signatureDataUrl}
                                            alt={`Signature ${idx + 1}`}
                                            sx={{
                                              width: "100%",
                                              height: 120,
                                              objectFit: "contain",
                                              backgroundColor: "grey.100",
                                              border: "1px solid",
                                              borderColor: "divider",
                                              borderRadius: 1,
                                              cursor: "pointer",
                                              transition:
                                                "transform 0.2s, box-shadow 0.2s",
                                              "&:hover": {
                                                transform: "scale(1.02)",
                                                boxShadow: 1,
                                              },
                                            }}
                                            onClick={() => {
                                              // Open full signature in new window
                                              if (signatureDataUrl) {
                                                window.open(
                                                  signatureDataUrl,
                                                  "_blank",
                                                );
                                              }
                                            }}
                                          />
                                          {/* Signature context info */}
                                          {item.locationName && (
                                            <Box sx={{ pt: 0.5 }}>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                  display: "block",
                                                  mb: 0.3,
                                                }}
                                              >
                                                {item.callDate}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  display: "block",
                                                  fontWeight: 600,
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                }}
                                              >
                                                {item.locationName} (
                                                {item.locationNumber})
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block" }}
                                              >
                                                {item.city}, {item.province}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block" }}
                                              >
                                                {item.userName}
                                              </Typography>
                                              {name && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    display: "block",
                                                    mt: 0.3,
                                                    fontStyle: "italic",
                                                  }}
                                                >
                                                  Signed by: {name}
                                                  {position && ` (${position})`}
                                                </Typography>
                                              )}
                                            </Box>
                                          )}
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Showing {Math.min(12, qa.data.length)} of{" "}
                                  {qa.data.length} signatures
                                </Typography>
                              </Box>
                            )}
                        </Paper>
                      ))}
                    </Stack>
                  )}
              </>
            );
          })()}
        </Paper>
      </Stack>
    </Page>
  );
};

export default SurveyAnalysis;
